export type AnimationSpeed = 0.5 | 1 | 2;
export type StorySpeed = 0.8 | 1 | 1.35;

export interface AppSettings {
  masterVolume: number;
  bgmVolume: number;
  seVolume: number;
  animationSpeed: AnimationSpeed;
  storySpeed: StorySpeed;
}

export const DEFAULT_SETTINGS: AppSettings = Object.freeze({
  masterVolume: 80,
  bgmVolume: 65,
  seVolume: 80,
  animationSpeed: 1,
  storySpeed: 1,
});

const STORAGE_KEY = "yoake-phaser-settings-v1";

export class AppSettingsStore {
  private current: AppSettings = loadSettings();
  private readonly listeners = new Set<(settings: AppSettings) => void>();

  public get value(): AppSettings {
    return { ...this.current };
  }

  public update(patch: Partial<AppSettings>): void {
    this.current = normalizeSettings({ ...this.current, ...patch });
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(this.current));
    } catch {
      // Settings remain active for the current session when storage is unavailable.
    }
    for (const listener of this.listeners) listener(this.value);
  }

  public reset(): void {
    this.update(DEFAULT_SETTINGS);
  }

  public subscribe(listener: (settings: AppSettings) => void): () => void {
    this.listeners.add(listener);
    listener(this.value);
    return () => this.listeners.delete(listener);
  }
}

function loadSettings(): AppSettings {
  try {
    const stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? "null") as Partial<AppSettings> | null;
    return stored ? normalizeSettings({ ...DEFAULT_SETTINGS, ...stored }) : { ...DEFAULT_SETTINGS };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

function normalizeSettings(settings: AppSettings): AppSettings {
  return {
    masterVolume: clampVolume(settings.masterVolume),
    bgmVolume: clampVolume(settings.bgmVolume),
    seVolume: clampVolume(settings.seVolume),
    animationSpeed: settings.animationSpeed === 0.5 || settings.animationSpeed === 2
      ? settings.animationSpeed
      : 1,
    storySpeed: settings.storySpeed === 0.8 || settings.storySpeed === 1.35
      ? settings.storySpeed
      : 1,
  };
}

function clampVolume(value: number): number {
  return Math.round(Math.max(0, Math.min(100, Number(value) || 0)));
}
